import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'sonner';
import { validateAdminTranslations } from '../utils/translationValidator';
import { SelectField } from '../components/SelectField';
import BackButton from '../components/BackButton';
import { getAdCoverImage } from '../lib/adImages';
import { toAbsoluteUrl } from '../lib/media';
import { getApiBase } from '../api/httpClient';
import '../components/brand.css';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { 
  Shield, Users, AlertTriangle, FileText, 
  CheckCircle, XCircle, Ban, Unlock, RefreshCw,
  Eye, ChevronLeft, ChevronRight, Plus, Search,
  TrendingDown, AlertCircle, Clock, Image, Edit,
  History, MessageSquare, ExternalLink,
  BarChart3, ShoppingBag, Briefcase, BookOpen, EyeOff,
  CreditCard, Receipt, ArrowLeft, LogOut, Home, Activity
} from 'lucide-react';
import './AdminModeration.css';

// iter267: same-origin smart base — on production custom domains
// `process.env.REACT_APP_BACKEND_URL` is cross-origin (preview host) and CORS
// blocks every admin fetch, returning silent zeros. getApiBase() picks ''
// (relative URL) on production hosts, env URL on preview/dev.
const API_BASE = getApiBase();

function AdminModeration() {
  const { t } = useTranslation();
  const { lang } = useLanguage();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const isArabic = lang === 'ar';

  // Validate translations in development mode
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      validateAdminTranslations(t);
    }
  }, [t]);

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const tabsRef = useRef(null);
  const location = useLocation();

  // Helper: switch to a section, scroll the tab strip into view, and apply
  // an optional filter. Replaces manual scrolling after KPI card clicks. (P2)
  const goToSection = useCallback((tab, opts = {}) => {
    setActiveTab(tab);
    if (opts.queueFilter !== undefined) setQueueFilter(opts.queueFilter);
    if (opts.bannedOnly !== undefined) setShowBannedOnly(opts.bannedOnly);
    // Scroll tab strip + active tab into view — defer so React renders the new tab
    requestAnimationFrame(() => {
      if (tabsRef.current?.scrollIntoView) {
        tabsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      // Center the now-active tab button inside the horizontally-scrollable strip
      requestAnimationFrame(() => {
        const activeBtn = tabsRef.current?.querySelector?.('.tab-btn.active');
        activeBtn?.scrollIntoView?.({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      });
    });
  }, []);

  // Honor ?section=... ?status=... query params so external links / KPI tiles
  // on the Premium dashboard can deep-link into this legacy page reliably. (P2)
  useEffect(() => {
    const hash = location.hash || window.location.hash || '';
    const qIdx = hash.indexOf('?');
    const params = new URLSearchParams(qIdx >= 0 ? hash.slice(qIdx + 1) : (location.search || ''));
    const sect = params.get('section');
    if (!sect) return;
    const map = { users: 'users', ads: 'queue', queue: 'queue', reports: 'reports', payments: 'payments', banned: 'users', blocklist: 'blocklist', books: 'books', overview: 'overview' };
    const target = map[sect.toLowerCase()];
    if (!target) return;
    const status = params.get('status');
    const bannedOnly = sect.toLowerCase() === 'banned';
    goToSection(target, { queueFilter: status || undefined, bannedOnly });
  }, [location, goToSection]);
  
  // Review Queue State (Ads)
  const [queueAds, setQueueAds] = useState([]);
  const [queueTotal, setQueueTotal] = useState(0);
  
  // Books Queue State
  const [queueBooks, setQueueBooks] = useState([]);
  const [queueBooksTotal, setQueueBooksTotal] = useState(0);
  
  // Reports State
  const [reports, setReports] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(0);
  
  // Users State
  const [users, setUsers] = useState([]);
  const [usersTotal, setUsersTotal] = useState(0);
  const [userSort, setUserSort] = useState('trust_score');
  const [userSortOrder, setUserSortOrder] = useState('asc');
  const [userSearch, setUserSearch] = useState('');
  const [debouncedUserSearch, setDebouncedUserSearch] = useState('');
  
  // Debounce user search 300ms so we don't refetch on every keystroke
  // (refetch unmounts/remounts list items which kills input focus).
  useEffect(() => {
    const t = setTimeout(() => setDebouncedUserSearch(userSearch), 300);
    return () => clearTimeout(t);
  }, [userSearch]);
  const [showBannedOnly, setShowBannedOnly] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // Blocklist State
  const [blocklist, setBlocklist] = useState({ hard: [], soft: [] });
  const [newWord, setNewWord] = useState('');
  const [newWordType, setNewWordType] = useState('soft');

  // Payments State
  const [payments, setPayments] = useState([]);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsStats, setPaymentsStats] = useState({ pending: 0, approved: 0, rejected: 0 });

  // Payment Reject Modal
  const [paymentRejectModalOpen, setPaymentRejectModalOpen] = useState(false);
  const [paymentToReject, setPaymentToReject] = useState(null);
  const [paymentRejectReason, setPaymentRejectReason] = useState('');
  const [paymentRejectError, setPaymentRejectError] = useState('');

  // Ad Details Drawer
  const [selectedAdForDetails, setSelectedAdForDetails] = useState(null);
  const [adDetailsLoading, setAdDetailsLoading] = useState(false);
  const [adModerationHistory, setAdModerationHistory] = useState([]);
  const [adReports, setAdReports] = useState([]);

  // Enhanced Moderation State
  const [queueFilter, setQueueFilter] = useState('all_pending'); // all_pending, held, limited, hidden, all
  const [queueSortBy, setQueueSortBy] = useState('risk_score');
  const [queueSortOrder, setQueueSortOrder] = useState('desc');
  const [moderationStats, setModerationStats] = useState(null);
  const [riskThresholds, setRiskThresholds] = useState({ HOLD: 70, LIMIT: 40 });
  
  // Ban Modal State
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [banTargetUser, setBanTargetUser] = useState(null);
  const [banType, setBanType] = useState('TEMP');
  const [banDuration, setBanDuration] = useState(7);
  const [banReason, setBanReason] = useState('');

  // Report Validation State
  const [reportToValidate, setReportToValidate] = useState(null);
  const [reportValidationNote, setReportValidationNote] = useState('');

  // Audit Log State
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLogsTotal, setAuditLogsTotal] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Reject Modal
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [adToReject, setAdToReject] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [rejectError, setRejectError] = useState('');

  // Ban Confirmation
  const [banConfirmOpen, setBanConfirmOpen] = useState(false);
  const [userToBan, setUserToBan] = useState(null);

  // Generic item moderation state
  const [itemToModerate, setItemToModerate] = useState(null);
  const [moderateType, setModerateType] = useState(null);
  const [moderateAction, setModerateAction] = useState(null);

  // Define all callbacks BEFORE any conditional returns
  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      // iter280 — use the canonical /api/admin/dashboard-stats endpoint
      // so numbers match direct DB aggregation. Adapt the new envelope
      // back to the legacy shape this component's JSX expects so we
      // don't touch 80+ render lines.
      const response = await fetch(`${API_BASE}/api/admin/dashboard-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const live = await response.json();
        const adapted = {
          users: {
            total:        live.users?.total_users ?? 0,
            active:       live.users?.active_users ?? 0,
            banned:       live.users?.banned_users ?? 0,
            admins:       live.users?.admin_users ?? 0,
            normal_users: live.users?.normal_users ?? 0,
            new_7d:       live.users?.new_users_7_days ?? 0,
            new_30d:      live.users?.new_users_30_days ?? 0,
            // iter283 — 3-pill diagnostic.
            real_users:   live.users?.real_users ?? 0,
            test_users:   live.users?.test_users ?? 0,
            seed_users:   live.users?.seed_users ?? 0,
            raw_total:    live.users?.raw_total_users ?? 0,
          },
          listings: {
            ads: {
              total:    live.ads?.total_ads ?? 0,
              approved: live.ads?.public_active_ads ?? 0,
              pending:  live.ads?.pending_ads ?? 0,
              draft:    live.ads?.draft_ads ?? 0,
              hidden:   (live.ads?.hidden_by_user_ads ?? 0) + (live.ads?.hidden_by_reports_ads ?? 0),
              rejected: live.ads?.rejected_ads ?? 0,
              removed:  live.ads?.removed_ads ?? 0,
              sold:     live.ads?.sold_ads ?? 0,
              published_alias: live.ads?.published_alias_ads ?? 0,
              // iter286 — Legacy queue is SEPARATE from total; surfaced
              // via its own card. Keep the value on the listings.ads
              // namespace for backwards compatibility with any consumer.
              needs_photo_reupload: live.ads?.needs_photo_reupload_ads ?? 0,
              legacy_repair_queue:  live.ads?.legacy_repair_queue ?? 0,
              deleted_excluded:     live.ads?.deleted_excluded ?? 0,
              // iter283 — 3-pill diagnostic.
              test_seed: live.ads?.test_seed_ads ?? 0,
              raw_total: live.ads?.raw_db_ads ?? 0,
            },
            books: { pending: 0 },  // not surfaced via this endpoint
          },
          reports: {
            pending:   live.reports?.pending_reports ?? 0,
            total:     live.reports?.total_reports ?? 0,
            valid:     live.reports?.valid_reports ?? 0,
            invalid:   live.reports?.invalid_reports ?? 0,
            dismissed: live.reports?.dismissed_reports ?? 0,
            // iter283 — test/seed reports surfaced separately.
            test_seed: live.reports?.test_seed_reports ?? 0,
          },
          payments: {
            total:    live.payments?.total_payment_requests ?? 0,
            pending:  live.payments?.pending_payments ?? 0,
            approved: live.payments?.approved_payments ?? 0,
            rejected: live.payments?.rejected_payments ?? 0,
          },
          // iter283 — pass through the attention reasons + policy snapshot.
          attention_reasons:    live.attention_reasons || [],
          attention_reasons_ar: live.attention_reasons_ar || [],
          all_clear:            !!live.all_clear,
          posting_policy:       live.posting_policy || null,
          _source: live._source,
        };
        setStats(adapted);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  const fetchQueue = useCallback(async () => {
    if (!token) return;
    try {
      // Use enhanced moderation queue endpoint
      const params = new URLSearchParams({
        status: queueFilter,
        sort_by: queueSortBy,
        sort_order: queueSortOrder,
        limit: '50'
      });
      const response = await fetch(`${API_BASE}/api/admin/moderation/enhanced-queue?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQueueAds(data.ads || []);
        setQueueTotal(data.total || 0);
        if (data.stats) {
          setModerationStats(data.stats);
        }
        if (data.thresholds) {
          setRiskThresholds(data.thresholds);
        }
      }
    } catch (error) {
      console.error('Failed to fetch queue:', error);
    }
  }, [token, queueFilter, queueSortBy, queueSortOrder]);

  const fetchBooks = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/api/admin/moderation?type=books&status=pending&limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setQueueBooks(data.items || []);
        setQueueBooksTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch books:', error);
    }
  }, [token]);

  const fetchReports = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/api/admin/reports?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
        setReportsTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch reports:', error);
    }
  }, [token]);

  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      let url = `${API_BASE}/api/admin/users?limit=50&sort_by=${userSort}&sort_order=${userSortOrder}`;
      if (debouncedUserSearch) {
        url += `&search=${encodeURIComponent(debouncedUserSearch)}`;
      }
      if (showBannedOnly) {
        url += `&banned_only=true`;
      }
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
        setUsersTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }, [token, userSort, userSortOrder, debouncedUserSearch, showBannedOnly]);

  const fetchBlocklist = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/api/admin/blocklist`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBlocklist(data.blocklist || { hard: [], soft: [] });
      }
    } catch (error) {
      console.error('Failed to fetch blocklist:', error);
    }
  }, [token]);

  const fetchPayments = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/api/admin/payments/pending`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPayments(data.items || []);
        setPaymentsTotal(data.total || 0);
        setPaymentsStats(data.stats || { pending: 0, approved: 0, rejected: 0 });
      }
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    }
  }, [token]);

  // Fetch enhanced moderation stats
  const fetchModerationStats = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch(`${API_BASE}/api/admin/moderation/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setModerationStats(data);
        if (data.thresholds) {
          setRiskThresholds(data.thresholds);
        }
      }
    } catch (error) {
      console.error('Failed to fetch moderation stats:', error);
    }
  }, [token]);

  // Fetch audit logs
  const fetchAuditLogs = useCallback(async (entityId = null) => {
    if (!token) return;
    try {
      let url = `${API_BASE}/api/admin/moderation/audit-log?limit=50`;
      if (entityId) {
        url += `&entity_id=${entityId}`;
      }
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAuditLogs(data.logs || []);
        setAuditLogsTotal(data.total || 0);
      }
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    }
  }, [token]);

  // Check admin access - redirect if not admin
  useEffect(() => {
    if (user && !user.is_admin) {
      toast.error(t('admin.accessDenied'));
      navigate('/account');
    }
  }, [user, navigate, t]);

  // Load data when component mounts
  useEffect(() => {
    const loadData = async () => {
      if (!token || !user?.is_admin) return;
      setLoading(true);
      await Promise.all([fetchStats(), fetchQueue(), fetchBooks(), fetchReports(), fetchUsers(), fetchBlocklist(), fetchPayments(), fetchModerationStats()]);
      setLoading(false);
    };
    loadData();
  }, [token, user, fetchStats, fetchQueue, fetchBooks, fetchReports, fetchUsers, fetchBlocklist, fetchPayments, fetchModerationStats]);

  // Refetch users when DEBOUNCED search/filter changes — keystrokes update
  // userSearch locally for instant typing feedback; the debounced value
  // (300ms) drives the API call so the list does not flicker and the input
  // never loses focus.
  useEffect(() => {
    if (token && user?.is_admin) {
      fetchUsers();
    }
  }, [debouncedUserSearch, showBannedOnly, fetchUsers, token, user]);

  // Refetch queue when filter/sort changes
  useEffect(() => {
    if (token && user?.is_admin) {
      fetchQueue();
    }
  }, [queueFilter, queueSortBy, queueSortOrder, fetchQueue, token, user]);

  // ===== GENERIC MODERATION ACTIONS =====
  const askConfirmEnhanced = (adId, action) => {
    const labels = {
      APPROVE: isArabic ? 'موافقة على هذا الإعلان؟' : 'Approve this ad?',
      REJECT:  isArabic ? 'رفض هذا الإعلان؟ السبب مطلوب.' : 'Reject this ad? A reason is required.',
      HIDE:    isArabic ? 'إخفاء هذا الإعلان من العامة؟' : 'Hide this ad from public visibility?',
      UNHIDE:  isArabic ? 'إعادة إظهار هذا الإعلان؟' : 'Restore this ad to public visibility?',
      LIMIT:   isArabic ? 'تقييد هذا الإعلان؟ السبب مطلوب.' : 'Limit this ad? A reason is required.',
      UNLIMIT: isArabic ? 'إلغاء تقييد هذا الإعلان؟' : 'Unlimit this ad?',
      DELETE:  isArabic ? 'حذف هذا الإعلان؟ السبب مطلوب.' : 'Delete this ad? A reason is required.',
    };
    const requiresReason = ['REJECT', 'LIMIT', 'HIDE', 'DELETE'].includes(action);
    setActionReason('');
    setPendingAction({
      kind: 'enhanced',
      enhancedAction: action,
      adId,
      label: labels[action] || `Confirm ${action}?`,
      requiresReason,
    });
  };

  // 2026-02-27 — Confirmation gate for destructive moderation actions.
  // pendingAction shape: { kind: 'approve'|'reject'|'hide', itemType, itemId, isHidden, label, requiresReason }
  const [pendingAction, setPendingAction] = useState(null);
  const [actionReason, setActionReason] = useState('');
  const [actionSubmitting, setActionSubmitting] = useState(false);

  const askConfirm = (kind, itemType, itemId, extra = {}) => {
    const labels = {
      approve: isArabic ? 'موافقة على هذا الإعلان وجعله مرئيًا للعموم؟' : 'Approve this ad and make it live?',
      reject:  isArabic ? 'رفض هذا الإعلان؟ سيتم إخطار صاحب الإعلان.' : 'Reject this ad? The owner will be notified.',
      hide:    isArabic ? 'إخفاء هذا الإعلان من المتجر العام؟' : 'Hide this ad from the public shop?',
      unhide:  isArabic ? 'إعادة إظهار هذا الإعلان للعموم؟' : 'Restore this ad to public visibility?',
    };
    const labelKey = kind === 'hide' && extra.isHidden === false ? 'unhide' : kind;
    setActionReason('');
    setPendingAction({
      kind,
      itemType,
      itemId,
      isHidden: extra.isHidden,
      label: labels[labelKey],
      requiresReason: kind === 'reject' || (kind === 'hide' && extra.isHidden === true),
    });
  };

  const runPendingAction = async () => {
    if (!pendingAction) return;
    if (pendingAction.requiresReason && !actionReason.trim()) {
      toast.error(isArabic ? 'السبب مطلوب' : 'Reason is required');
      return;
    }
    setActionSubmitting(true);
    try {
      if (pendingAction.kind === 'approve')        await handleApproveItem(pendingAction.itemType, pendingAction.itemId);
      else if (pendingAction.kind === 'reject')    await handleRejectItem(pendingAction.itemType, pendingAction.itemId, actionReason.trim());
      else if (pendingAction.kind === 'hide')      await handleHideItem(pendingAction.itemType, pendingAction.itemId, !!pendingAction.isHidden, actionReason.trim());
      else if (pendingAction.kind === 'enhanced')  await handleEnhancedAction(pendingAction.adId, pendingAction.enhancedAction, actionReason.trim(), '');
      else if (pendingAction.kind === 'user_action') await handleUserAction(pendingAction.userId, pendingAction.userAction, actionReason.trim());
    } finally {
      setActionSubmitting(false);
      setPendingAction(null);
      setActionReason('');
    }
  };

  const handleApproveItem = async (itemType, itemId) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/moderation/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type: itemType, id: itemId })
      });
      
      let body = {};
      try { body = await response.json(); } catch (_) {}
      if (response.ok) {
        toast.success(t('admin.approveSuccess'));
        if (itemType === 'books') fetchBooks();
        else fetchQueue();
        fetchStats();
      } else {
        const reason = typeof body?.detail === 'string' ? body.detail : `HTTP ${response.status}`;
        toast.error(`${t('admin.approveFailed')}: ${reason}`);
      }
    } catch (error) {
      console.error('Approve error:', error);
      toast.error(`${t('admin.approveFailed')}: ${error?.message || 'network'}`);
    }
  };

  const handleRejectItem = async (itemType, itemId, reason = '') => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/moderation/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type: itemType, id: itemId, reason })
      });
      
      let body = {};
      try { body = await response.json(); } catch (_) {}
      if (response.ok) {
        toast.success(t('admin.rejectSuccess'));
        if (itemType === 'books') fetchBooks();
        else fetchQueue();
        fetchStats();
      } else {
        const reasonMsg = typeof body?.detail === 'string' ? body.detail : `HTTP ${response.status}`;
        toast.error(`${t('admin.rejectFailed')}: ${reasonMsg}`);
      }
    } catch (error) {
      console.error('Reject error:', error);
      toast.error(`${t('admin.rejectFailed')}: ${error?.message || 'network'}`);
    }
  };

  const handleHideItem = async (itemType, itemId, isHidden = true, reason = '') => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/moderation/hide`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ type: itemType, id: itemId, is_hidden: isHidden, reason })
      });
      
      let body = {};
      try { body = await response.json(); } catch (_) {}
      if (response.ok) {
        toast.success(isHidden ? t('admin.hideSuccess') : t('admin.unhideSuccess'));
        if (itemType === 'books') fetchBooks();
        else fetchQueue();
        fetchStats();
      } else {
        const reasonMsg = typeof body?.detail === 'string' ? body.detail : `HTTP ${response.status}`;
        toast.error(`${t('admin.hideFailed')}: ${reasonMsg}`);
      }
    } catch (error) {
      console.error('Hide error:', error);
      toast.error(`${t('admin.hideFailed')}: ${error?.message || 'network'}`);
    }
  };

  // ===== ENHANCED MODERATION ACTIONS =====
  const handleEnhancedAction = async (adId, action, reason = '', note = '') => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/moderation/enhanced-action`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ad_id: adId, action, reason, note })
      });
      
      if (response.ok) {
        const actionMessages = {
          'APPROVE': t('admin.approveSuccess'),
          'REJECT': t('admin.rejectSuccess'),
          'DELETE': t('admin.deleteSuccess'),
          'HIDE': t('admin.hideSuccess'),
          'UNHIDE': t('admin.unhideSuccess'),
          'LIMIT': isArabic ? 'تم تقييد الإعلان' : 'Ad limited successfully',
          'UNLIMIT': isArabic ? 'تم إلغاء تقييد الإعلان' : 'Ad unlimited successfully',
        };
        toast.success(actionMessages[action] || t('admin.actionSuccess'));
        fetchQueue();
        fetchStats();
        fetchModerationStats();
        if (selectedAdForDetails) {
          setSelectedAdForDetails(null);
        }
      } else {
        const data = await response.json();
        toast.error(data.detail || t('admin.actionFailed'));
      }
    } catch (error) {
      console.error('Enhanced action error:', error);
      toast.error(t('admin.actionFailed'));
    }
  };

  const handleValidateReport = async (reportId, isValid) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/moderation/report-feedback`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          report_id: reportId, 
          is_valid: isValid,
          note: reportValidationNote 
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(
          isValid 
            ? (isArabic ? 'تم التحقق من البلاغ - ثقة المبلّغ: ' + data.new_trust : 'Report validated - Reporter trust: ' + data.new_trust)
            : (isArabic ? 'تم رفض البلاغ - ثقة المبلّغ: ' + data.new_trust : 'Report invalidated - Reporter trust: ' + data.new_trust)
        );
        fetchReports();
        fetchQueue();
        setReportToValidate(null);
        setReportValidationNote('');
      } else {
        const data = await response.json();
        toast.error(data.detail || (isArabic ? 'فشل التحقق من البلاغ' : 'Failed to validate report'));
      }
    } catch (error) {
      console.error('Validate report error:', error);
      toast.error(isArabic ? 'فشل التحقق من البلاغ' : 'Failed to validate report');
    }
  };

  const handleBanUser = async () => {
    if (!banTargetUser || !banReason.trim()) {
      toast.error(isArabic ? 'يرجى إدخال سبب الحظر' : 'Please enter a ban reason');
      return;
    }
    // Prevent admin from banning themselves accidentally
    if (banTargetUser.user_id && user?.user_id && banTargetUser.user_id === user.user_id) {
      toast.error(isArabic ? 'لا يمكنك حظر حسابك الإداري الخاص' : "You can't ban your own admin account");
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/moderation/ban-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          user_id: banTargetUser.user_id,
          ban_type: banType,
          duration_days: banType === 'TEMP' ? banDuration : null,
          reason: banReason
        })
      });
      
      if (response.ok) {
        toast.success(isArabic ? 'تم حظر المستخدم بنجاح' : 'User banned successfully');
        setBanModalOpen(false);
        setBanTargetUser(null);
        setBanReason('');
        fetchUsers();
        fetchQueue();
      } else {
        const data = await response.json();
        toast.error(data.detail || (isArabic ? 'فشل حظر المستخدم' : 'Failed to ban user'));
      }
    } catch (error) {
      console.error('Ban user error:', error);
      toast.error(isArabic ? 'فشل حظر المستخدم' : 'Failed to ban user');
    }
  };

  const handleUnbanUser = async (userId) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/moderation/unban-user`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: userId })
      });
      
      if (response.ok) {
        toast.success(isArabic ? 'تم إلغاء حظر المستخدم' : 'User unbanned successfully');
        fetchUsers();
      } else {
        const data = await response.json();
        toast.error(data.detail || (isArabic ? 'فشل إلغاء الحظر' : 'Failed to unban user'));
      }
    } catch (error) {
      console.error('Unban user error:', error);
      toast.error(isArabic ? 'فشل إلغاء الحظر' : 'Failed to unban user');
    }
  };

  const openBanModal = (targetUser) => {
    setBanTargetUser(targetUser);
    setBanType('TEMP');
    setBanDuration(7);
    setBanReason('');
    setBanModalOpen(true);
  };

  // Helper function to get risk level color and label
  const getRiskLevel = (score) => {
    if (score >= riskThresholds.HOLD) {
      return { level: 'high', color: '#EF4444', label: isArabic ? 'عالي' : 'High' };
    } else if (score >= riskThresholds.LIMIT) {
      return { level: 'medium', color: '#F59E0B', label: isArabic ? 'متوسط' : 'Medium' };
    }
    return { level: 'low', color: '#10B981', label: isArabic ? 'منخفض' : 'Low' };
  };

  // Helper function to get risk factor label
  const getRiskFactorLabel = (factor) => {
    const labels = {
      // Keywords
      'WEAPONS': isArabic ? 'أسلحة' : 'Weapons',
      'DRUGS': isArabic ? 'مخدرات' : 'Drugs',
      'FAKE_DOCS': isArabic ? 'مستندات مزورة' : 'Fake Documents',
      'STOLEN_GOODS': isArabic ? 'بضائع مسروقة' : 'Stolen Goods',
      'SCAM': isArabic ? 'احتيال' : 'Scam',
      'ADULT_CONTENT': isArabic ? 'محتوى بالغ' : 'Adult Content',
      'HACKING': isArabic ? 'قرصنة' : 'Hacking',
      'FINANCIAL_FRAUD': isArabic ? 'احتيال مالي' : 'Financial Fraud',
      // Behavior
      'NEW_ACCOUNT': isArabic ? 'حساب جديد' : 'New Account',
      'BURST_POSTING': isArabic ? 'نشر متكرر' : 'Burst Posting',
      'MISSING_LOCATION': isArabic ? 'موقع مفقود' : 'Missing Location',
      'EXTERNAL_LINK': isArabic ? 'رابط خارجي' : 'External Link',
      'NO_IMAGES': isArabic ? 'بدون صور' : 'No Images',
      'SHORT_DESCRIPTION': isArabic ? 'وصف قصير' : 'Short Description',
      'SHORT_TITLE': isArabic ? 'عنوان قصير' : 'Short Title',
      // Image
      'BLURRY_IMAGE': isArabic ? 'صورة ضبابية' : 'Blurry Image',
      'DARK_IMAGE': isArabic ? 'صورة مظلمة' : 'Dark Image',
      // Reports
      'USER_REPORTS': isArabic ? 'بلاغات المستخدمين' : 'User Reports',
      // Legacy flags
      'missing_location': isArabic ? 'موقع مفقود' : 'Missing Location',
      'no_images': isArabic ? 'بدون صور' : 'No Images',
      'low_quality_images': isArabic ? 'صور منخفضة الجودة' : 'Low Quality Images',
      'short_title': isArabic ? 'عنوان قصير' : 'Short Title',
      'short_description': isArabic ? 'وصف قصير' : 'Short Description',
      'needs_images': isArabic ? 'يحتاج صور' : 'Needs Images',
    };
    return labels[factor] || factor;
  };

  // ===== PAYMENT VERIFICATION ACTIONS =====
  const handleApprovePayment = async (requestId) => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/payments/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ request_id: requestId, action: 'approve' })
      });
      
      if (response.ok) {
        toast.success(t('admin.paymentApproved'));
        fetchPayments();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || t('admin.paymentApproveFailed'));
      }
    } catch (error) {
      console.error('Approve payment error:', error);
      toast.error(t('admin.paymentApproveFailed'));
    }
  };

  const handleRejectPayment = async (requestId, reason = '') => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/payments/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ request_id: requestId, action: 'reject', rejection_reason: reason || 'Rejected by admin' })
      });
      
      if (response.ok) {
        toast.success(t('admin.paymentRejected'));
        setPaymentRejectModalOpen(false);
        setPaymentToReject(null);
        setPaymentRejectReason('');
        setPaymentRejectError('');
        fetchPayments();
        fetchStats();
      } else {
        const data = await response.json();
        toast.error(data.detail || t('admin.paymentRejectFailed'));
      }
    } catch (error) {
      console.error('Reject payment error:', error);
      toast.error(t('admin.paymentRejectFailed'));
    }
  };

  const openPaymentRejectModal = (payment) => {
    setPaymentToReject(payment);
    setPaymentRejectReason('');
    setPaymentRejectError('');
    setPaymentRejectModalOpen(true);
  };

  const handlePaymentRejectWithReason = () => {
    if (!paymentRejectReason.trim()) {
      setPaymentRejectError(t('admin.rejectReasonRequired'));
      return;
    }
    handleRejectPayment(paymentToReject.request_id, paymentRejectReason);
  };

  // Fetch ad details for drawer
  const fetchAdDetails = async (ad) => {
    setSelectedAdForDetails(ad);
    setAdDetailsLoading(true);
    setCurrentImageIndex(0);
    
    try {
      // Fetch full ad details
      const adResponse = await fetch(`${API_BASE}/api/ads/${ad.ad_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (adResponse.ok) {
        const adData = await adResponse.json();
        setSelectedAdForDetails(adData.ad || ad);
      }

      // Fetch moderation history (if endpoint exists)
      try {
        const historyResponse = await fetch(`${API_BASE}/api/admin/ads/${ad.ad_id}/history`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          setAdModerationHistory(historyData.history || []);
        } else {
          setAdModerationHistory([]);
        }
      } catch {
        setAdModerationHistory([]);
      }

      // Fetch reports for this ad
      try {
        const reportsResponse = await fetch(`${API_BASE}/api/admin/reports?ad_id=${ad.ad_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (reportsResponse.ok) {
          const reportsData = await reportsResponse.json();
          setAdReports(reportsData.reports || []);
        } else {
          setAdReports([]);
        }
      } catch {
        setAdReports([]);
      }
    } catch (error) {
      console.error('Failed to fetch ad details:', error);
    } finally {
      setAdDetailsLoading(false);
    }
  };

  // Ad Actions
  const handleAdAction = async (adId, action, note = '') => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/ads/review`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ad_id: adId, action, note })
      });
      
      if (response.ok) {
        toast.success(t('admin.actionSuccess'));
        fetchQueue();
        fetchStats();
        setSelectedAdForDetails(null);
        setRejectModalOpen(false);
        setAdToReject(null);
        setRejectNote('');
        setRejectError('');
      } else {
        throw new Error('Action failed');
      }
    } catch (error) {
      toast.error(t('admin.actionFailed'));
    }
  };

  // Open reject modal
  const openRejectModal = (ad) => {
    setAdToReject(ad);
    setRejectNote('');
    setRejectError('');
    setRejectModalOpen(true);
  };

  // Handle reject with required reason
  const handleRejectWithReason = () => {
    if (!rejectNote.trim()) {
      setRejectError(t('admin.rejectReasonRequired'));
      return;
    }
    handleAdAction(adToReject.ad_id, 'reject', rejectNote);
  };

  // User Actions — Unified to single existing endpoint /api/admin/users/action
  // (P6 root cause: legacy /api/admin/users/{id}/ban|/unban don't exist on the
  // backend, so the old code 404'd → generic "Action failed" toast.)
  const handleUserAction = async (userId, action, reason = '') => {
    // Self-ban guard
    if (action === 'ban' && user?.user_id === userId) {
      toast.error(isArabic ? 'لا يمكنك حظر حسابك الإداري' : "You can't ban your own admin account");
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/admin/users/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ user_id: userId, action, reason }),
      });

      let body = {};
      try { body = await response.json(); } catch (_) { /* non-JSON */ }

      if (response.ok) {
        const msgMap = {
          ban: isArabic ? 'تم حظر المستخدم' : 'User banned',
          unban: isArabic ? 'تم إلغاء حظر المستخدم' : 'User unbanned',
          reset_trust: isArabic ? 'تمت إعادة ضبط الثقة' : 'Trust score reset',
        };
        toast.success(msgMap[action] || t('admin.actionSuccess'));
        fetchUsers();
        fetchStats();
        setBanConfirmOpen(false);
        setUserToBan(null);
      } else {
        const reason = typeof body?.detail === 'string'
          ? body.detail
          : Array.isArray(body?.detail)
              ? body.detail.map(d => d?.msg || JSON.stringify(d)).join(', ')
              : `HTTP ${response.status}`;
        toast.error(`${action === 'unban' ? (isArabic ? 'فشل إلغاء الحظر' : 'Unban failed') : (action === 'reset_trust' ? (isArabic ? 'فشل إعادة الثقة' : 'Reset trust failed') : t('admin.actionFailed'))}: ${reason}`);
      }
    } catch (error) {
      toast.error(`${t('admin.actionFailed')}: ${error?.message || (isArabic ? 'تعذّر الاتصال بالخادم' : 'network error')}`);
    }
  };

  // Confirmation wrapper for unban + reset_trust (P6)
  const askConfirmUserAction = (userId, action, userLabel = '') => {
    const labels = {
      unban: isArabic
        ? `إلغاء حظر هذا المستخدم${userLabel ? ` (${userLabel})` : ''} واستعادة وصوله للحساب؟`
        : `Unban this user${userLabel ? ` (${userLabel})` : ''} and restore account access?`,
      reset_trust: isArabic
        ? `إعادة ضبط درجة الثقة لهذا المستخدم${userLabel ? ` (${userLabel})` : ''}؟`
        : `Reset this user's trust score${userLabel ? ` (${userLabel})` : ''}?`,
    };
    setActionReason('');
    setPendingAction({
      kind: 'user_action',
      userId,
      userAction: action,
      label: labels[action] || `Confirm ${action}?`,
      requiresReason: false,
    });
  };

  // Report Actions — surfaces real backend error so the admin sees why a call failed
  const handleReportAction = async (reportId, action) => {
    if (!reportId) {
      toast.error(isArabic ? 'بلاغ غير صالح' : 'Invalid report — missing report_id');
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/admin/reports/action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ report_id: reportId, action })
      });

      let body = {};
      try { body = await response.json(); } catch (_) { /* non-JSON body */ }

      if (response.ok) {
        toast.success(t('admin.actionSuccess'));
        fetchReports();
        fetchStats();
        return;
      }

      // Surface the real backend reason (string detail OR validation array)
      let reason = '';
      if (typeof body?.detail === 'string') reason = body.detail;
      else if (Array.isArray(body?.detail)) reason = body.detail.map(d => d?.msg || JSON.stringify(d)).join(', ');
      else reason = `HTTP ${response.status}`;
      toast.error(`${t('admin.actionFailed')}: ${reason}`);
    } catch (error) {
      toast.error(`${t('admin.actionFailed')}: ${error?.message || (isArabic ? 'تعذّر الاتصال بالخادم' : 'network error')}`);
    }
  };

  // Add to blocklist
  const handleAddToBlocklist = async () => {
    if (!newWord.trim()) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/admin/blocklist/add`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ word: newWord.trim(), blocklist_type: newWordType })
      });
      
      if (response.ok) {
        toast.success(t('admin.actionSuccess'));
        fetchBlocklist();
        setNewWord('');
      } else {
        throw new Error('Failed to add');
      }
    } catch (error) {
      toast.error(t('admin.actionFailed'));
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      published: { color: 'green', icon: CheckCircle, label: t('admin.status.published') },
      limited: { color: 'yellow', icon: AlertTriangle, label: t('admin.status.limited') },
      under_review: { color: 'blue', icon: Clock, label: t('admin.status.under_review') },
      rejected: { color: 'red', icon: XCircle, label: t('admin.status.rejected') },
      draft: { color: 'gray', icon: FileText, label: t('admin.status.draft') }
    };
    return badges[status] || badges.draft;
  };

  const getTrustColor = (score) => {
    if (score >= 80) return 'trust-high';
    if (score >= 50) return 'trust-medium';
    if (score >= 30) return 'trust-low';
    return 'trust-critical';
  };

  const getImageUrl = (path) => {
    // 2026-02-27 — Delegate to the canonical resolver so admin sees the same
    // image as the public listing for every field/path variant.
    if (!path) return '/placeholder.svg';
    if (typeof path === 'object') {
      const cover = getAdCoverImage({ images: [path] });
      return cover || '/placeholder.svg';
    }
    try {
      const abs = toAbsoluteUrl(path);
      return abs || '/placeholder.svg';
    } catch (_) {
      return '/placeholder.svg';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(isArabic ? 'ar-SD' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Refresh all data
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      await Promise.all([
        fetchStats(),
        fetchQueue(),
        fetchBooks(),
        fetchReports(),
        fetchUsers(),
        fetchPayments(),
      ]);
      toast.success(isArabic ? 'تم التحديث' : 'Refreshed');
    } catch (e) {
      toast.error(isArabic ? 'فشل التحديث' : 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  // Get pending counts for badges
  const getPendingCount = (type) => {
    if (type === 'ads') return queueTotal;
    if (type === 'books') return queueBooksTotal;
    if (type === 'payments') return paymentsTotal;
    return 0;
  };

  // Format currency
  const formatCurrency = (amount, currency = 'SDG') => {
    if (!amount) return '-';
    return `${amount.toLocaleString()} ${currency}`;
  };

  // ===== RENDER SECTION =====
  // All hooks are called above, so conditional returns are safe here
  
  // Don't render if not admin
  if (!user?.is_admin) {
    return (
      <div className="admin-page" data-testid="admin-moderation">
        <div className="admin-access-denied">
          <Shield size={48} />
          <h2>{t('admin.accessDenied')}</h2>
          <p>{t('admin.notAuthorized')}</p>
          <button onClick={() => navigate('/account')} className="back-btn">
            {t('back')}
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="admin-page" data-testid="admin-moderation">
        <div className="admin-loading">
          <RefreshCw className="animate-spin" size={32} />
          <p>{t('admin.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page" dir={isArabic ? 'rtl' : 'ltr'} data-testid="admin-moderation">
      {/* Header — clean two-row admin command bar */}
      <header className="admin-header admin-header-v2" data-testid="admin-header">
        <div className="admin-header-row1">
          <div className="admin-header-title-block">
            <BackButton
              className="admin-back-btn"
              testId="admin-back-btn"
              size={20}
            />
            <span className="admin-header-shield"><Shield size={22} /></span>
            <h1 className="admin-header-title">{t('admin.title')}</h1>
          </div>
          <div className="admin-header-actions" role="toolbar" aria-label="Admin actions">
            <button
              type="button"
              className="admin-icon-btn"
              onClick={() => navigate('/admin/system-health')}
              data-testid="open-system-health-btn"
              title={isArabic ? 'صحة النظام' : 'System Health'}
              aria-label={isArabic ? 'صحة النظام' : 'System Health'}
            >
              <Activity size={18} />
            </button>
            <button
              type="button"
              className={`admin-icon-btn ${refreshing ? 'is-loading' : ''}`}
              onClick={handleRefresh}
              disabled={refreshing}
              aria-busy={refreshing}
              data-testid="refresh-btn"
              title={t('admin.refresh')}
              aria-label={t('admin.refresh')}
            >
              <RefreshCw size={18} className={refreshing ? 'spin' : ''} />
            </button>
            <button
              type="button"
              className="admin-icon-btn"
              onClick={() => navigate('/')}
              data-testid="exit-admin-btn"
              title={t('admin.exitAdmin')}
              aria-label={t('admin.exitAdmin')}
            >
              <Home size={18} />
            </button>
          </div>
        </div>
        <div className="admin-header-row2">
          {(() => {
            // iter283 — single source of truth: use the backend-computed
            // attention_reasons[]. If empty → All Clear; otherwise show
            // the FIRST reason explicitly so the operator never has to
            // guess what's wrong.
            const reasonsEn = stats?.attention_reasons || [];
            const reasonsAr = stats?.attention_reasons_ar || [];
            const healthy = !!stats?.all_clear;
            const reasons = isArabic ? reasonsAr : reasonsEn;
            const label = healthy
              ? (isArabic ? 'النظام سليم' : 'All Clear')
              : (reasons.length === 1
                  ? reasons[0]
                  : (isArabic
                      ? `يحتاج إلى انتباه (${reasons.length})`
                      : `Needs Attention (${reasons.length})`));
            return (
              <span
                className={`admin-health-pill ${healthy ? 'healthy' : 'attention'}`}
                data-testid="admin-health-pill"
                title={reasons.join(' · ')}
              >
                <span className="admin-health-dot" />
                {label}
              </span>
            );
          })()}
        </div>
      </header>

      {/* Stats Cards — clickable shortcuts */}
      {stats && (
        <div className="stats-cards">
          <button
            type="button"
            className="stat-card stat-card-btn"
            data-testid="stat-users"
            onClick={() => goToSection('users', { bannedOnly: false })}
            aria-label={t('admin.totalUsers')}
          >
            <div className="stat-icon blue">
              <Users size={24} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.users?.total || 0}</span>
              <span className="stat-label">{t('admin.totalUsers')}</span>
              <span className="stat-sub">+{stats.users?.new_7d || 0} {t('admin.new7d')}</span>
            </div>
          </button>
          <button
            type="button"
            className="stat-card stat-card-btn"
            data-testid="stat-pending"
            onClick={() => goToSection('queue', { queueFilter: 'all_pending' })}
            aria-label={t('admin.pendingReview')}
          >
            <div className="stat-icon yellow">
              <AlertTriangle size={24} />
            </div>
            <div className="stat-info">
              <span className="stat-value">
                {(stats.listings?.ads?.pending || 0) +
                 (stats.listings?.books?.pending || 0)}
              </span>
              <span className="stat-label">{t('admin.pendingReview')}</span>
            </div>
          </button>
          <button
            type="button"
            className="stat-card stat-card-btn"
            data-testid="stat-reports"
            onClick={() => goToSection('reports')}
            aria-label={t('admin.pendingReports')}
          >
            <div className="stat-icon red">
              <AlertCircle size={24} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.reports?.pending || 0}</span>
              <span className="stat-label">{t('admin.pendingReports')}</span>
            </div>
          </button>
          <button
            type="button"
            className="stat-card stat-card-btn"
            data-testid="stat-banned"
            onClick={() => goToSection('users', { bannedOnly: true })}
            aria-label={t('admin.bannedUsers')}
          >
            <div className="stat-icon purple">
              <Ban size={24} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.users?.banned || 0}</span>
              <span className="stat-label">{t('admin.bannedUsers')}</span>
            </div>
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="admin-tabs" ref={tabsRef}>
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
          data-testid="tab-overview"
        >
          <BarChart3 size={18} />
          {t('admin.overview')}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'queue' ? 'active' : ''}`}
          onClick={() => setActiveTab('queue')}
          data-testid="tab-queue"
        >
          <ShoppingBag size={18} />
          {t('admin.ads')}
          {queueTotal > 0 && <span className="badge">{queueTotal}</span>}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'books' ? 'active' : ''}`}
          onClick={() => setActiveTab('books')}
          data-testid="tab-books"
        >
          <BookOpen size={18} />
          {t('admin.books')}
          {queueBooksTotal > 0 && <span className="badge">{queueBooksTotal}</span>}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
          data-testid="tab-payments"
        >
          <CreditCard size={18} />
          {t('admin.payments')}
          {paymentsTotal > 0 && <span className="badge">{paymentsTotal}</span>}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveTab('reports')}
          data-testid="tab-reports"
        >
          <AlertTriangle size={18} />
          {t('admin.reports')}
          {reportsTotal > 0 && <span className="badge">{reportsTotal}</span>}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => { setActiveTab('users'); setShowBannedOnly(false); }}
          data-testid="tab-users"
        >
          <Users size={18} />
          {t('admin.users')}
        </button>
        <button 
          className={`tab-btn ${activeTab === 'blocklist' ? 'active' : ''}`}
          onClick={() => setActiveTab('blocklist')}
          data-testid="tab-blocklist"
        >
          <Ban size={18} />
          {t('admin.blocklist')}
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="overview-section">
            <h2 className="section-title">{t('admin.dashboardOverview')}</h2>
            
            {/* User Statistics — Total + breakdown grid */}
            <div className="overview-card">
              <h3><Users size={18} /> {t('admin.userStats')}</h3>
              <div className="overview-total" data-testid="overview-users-total">
                <span className="overview-total-label">{t('admin.totalUsers')}</span>
                <span className="overview-total-value">{stats.users?.total || 0}</span>
              </div>
              <div className="overview-grid">
                <button type="button" className="overview-stat overview-stat-btn" onClick={() => goToSection('users', { bannedOnly: false })}>
                  <span className="value">{stats.users?.active || 0}</span>
                  <span className="label">{isArabic ? 'مستخدمون نشطون' : 'Active Users'}</span>
                </button>
                <button type="button" className="overview-stat overview-stat-btn warning" data-testid="overview-stat-users-banned" onClick={() => goToSection('users', { bannedOnly: true })}>
                  <span className="value">{stats.users?.banned || 0}</span>
                  <span className="label">{t('admin.bannedUsers')}</span>
                </button>
                <div className="overview-stat">
                  <span className="value">{stats.users?.admins || 0}</span>
                  <span className="label">{isArabic ? 'مشرفون' : 'Admin Users'}</span>
                </div>
                <div className="overview-stat">
                  <span className="value">{stats.users?.normal_users || 0}</span>
                  <span className="label">{isArabic ? 'مستخدمون عاديون' : 'Normal Users'}</span>
                </div>
                <div className="overview-stat">
                  <span className="value">{stats.users?.new_7d || 0}</span>
                  <span className="label">
                    {t('admin.new7d')}
                    {(stats.users?.new_7d || 0) > 0 && (
                      <span className="overview-trend up" data-testid="overview-trend-users-7d">↑</span>
                    )}
                  </span>
                  {/* TODO(analytics): replace with sparkline once daily series endpoint exists */}
                </div>
                <div className="overview-stat">
                  <span className="value">{stats.users?.new_30d || 0}</span>
                  <span className="label">
                    {t('admin.new30d')}
                    {(stats.users?.new_30d || 0) > 0 && (
                      <span className="overview-trend up" data-testid="overview-trend-users-30d">↑</span>
                    )}
                  </span>
                </div>
              </div>
              {/* Contextual actions next to the User Stats data they belong to */}
              <div className="overview-card-actions" data-testid="overview-actions-users">
                <button
                  type="button"
                  className="overview-inline-btn"
                  onClick={() => goToSection('users', { bannedOnly: false })}
                  data-testid="overview-inline-manage-users"
                >
                  <Users size={14} /> {isArabic ? 'إدارة المستخدمين' : 'Manage Users'}
                </button>
                {(stats.users?.banned || 0) > 0 && (
                  <button
                    type="button"
                    className="overview-inline-btn danger"
                    onClick={() => goToSection('users', { bannedOnly: true })}
                    data-testid="overview-inline-view-banned"
                  >
                    <Ban size={14} /> {isArabic ? 'عرض المحظورين' : 'View Banned Users'}
                  </button>
                )}
              </div>
            </div>
            
            {/* Listing Statistics — Total + breakdown grid */}
            <div className="overview-card">
              <h3><ShoppingBag size={18} /> {t('admin.listingStats')}</h3>
              <div className="overview-total" data-testid="overview-ads-total">
                <span className="overview-total-label">{isArabic ? 'إجمالي الإعلانات' : 'Total Ads'}</span>
                <span className="overview-total-value">{stats.listings?.ads?.total || 0}</span>
              </div>
              <div className="overview-grid">
                <button
                  type="button"
                  className="overview-stat overview-stat-btn"
                  data-testid="overview-stat-ads-approved"
                  onClick={() => goToSection('queue', { queueFilter: 'approved' })}
                >
                  <span className="value">{stats.listings?.ads?.approved || 0}</span>
                  <span className="label">{isArabic ? 'منشورة (مباشر)' : 'Public Live'}</span>
                </button>
                <button
                  type="button"
                  className="overview-stat overview-stat-btn pending"
                  data-testid="overview-stat-ads-pending"
                  onClick={() => goToSection('queue', { queueFilter: 'all_pending' })}
                >
                  <span className="value">{stats.listings?.ads?.pending || 0}</span>
                  <span className="label">{t('admin.adsPending')}</span>
                </button>
                <button
                  type="button"
                  className="overview-stat overview-stat-btn"
                  data-testid="overview-stat-ads-draft"
                  onClick={() => goToSection('queue', { queueFilter: 'draft' })}
                >
                  <span className="value">{stats.listings?.ads?.draft || 0}</span>
                  <span className="label">{isArabic ? 'مسودة' : 'Draft'}</span>
                </button>
                <button
                  type="button"
                  className="overview-stat overview-stat-btn warning"
                  data-testid="overview-stat-ads-hidden"
                  onClick={() => goToSection('queue', { queueFilter: 'hidden' })}
                >
                  <span className="value">{stats.listings?.ads?.hidden || 0}</span>
                  <span className="label">{t('admin.adsHidden')}</span>
                </button>
                <button
                  type="button"
                  className="overview-stat overview-stat-btn"
                  data-testid="overview-stat-ads-rejected"
                  onClick={() => goToSection('queue', { queueFilter: 'rejected' })}
                >
                  <span className="value">{stats.listings?.ads?.rejected || 0}</span>
                  <span className="label">{t('admin.adsRejected')}</span>
                </button>
                <div className="overview-stat" data-testid="overview-stat-ads-removed">
                  <span className="value">{stats.listings?.ads?.removed || 0}</span>
                  <span className="label">{isArabic ? 'محذوف' : 'Removed'}</span>
                </div>
                {/* iter283 — explicit Sold tile so the math invariant
                    (sum(by_status) === total) holds. Sold ads are part
                    of total_ads on the backend. */}
                <div className="overview-stat" data-testid="overview-stat-ads-sold">
                  <span className="value">{stats.listings?.ads?.sold || 0}</span>
                  <span className="label">{isArabic ? 'مُباع' : 'Sold'}</span>
                </div>
              </div>
              {/* iter286 — diagnostic ribbon (live/test+seed/raw).
                  Needs Photo MOVED out of this card into its own
                  "Legacy Image Repair Queue" card below. */}
              <div
                className="overview-card-actions"
                data-testid="overview-ads-diagnostic"
                style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8, fontSize: 12 }}
              >
                <span className="admin-pill green" data-testid="overview-ads-diag-public">
                  {isArabic ? 'منشورة' : 'Public Live'}: <strong>{stats.listings?.ads?.approved || 0}</strong>
                </span>
                <span className="admin-pill gray" data-testid="overview-ads-diag-test-seed">
                  {isArabic ? 'اختبار/بذرة' : 'Test/Seed'}: <strong>{stats.listings?.ads?.test_seed || 0}</strong>
                </span>
                <span className="admin-pill blue" data-testid="overview-ads-diag-raw">
                  {isArabic ? 'الكل (خام)' : 'Raw DB'}: <strong>{stats.listings?.ads?.raw_total || 0}</strong>
                </span>
                <span style={{ color: 'var(--admin-text-dim, #6b7280)', alignSelf: 'center' }}>
                  · {isArabic
                      ? `إجمالي الإعلانات = منشورة + معلّقة + مسودة + مخفية + مرفوضة + محذوف + مُباع. طابور إصلاح الصور القديمة لا يُحسب هنا.`
                      : `Total Ads = Public Live + Pending + Draft + Hidden + Rejected + Removed + Sold. The Legacy Image Repair Queue is counted separately.`}
                </span>
              </div>
              {/* Contextual actions next to the Listing Stats data */}
              <div className="overview-card-actions" data-testid="overview-actions-ads">
                {(stats.listings?.ads?.pending || 0) > 0 && (
                  <button
                    type="button"
                    className="overview-inline-btn primary"
                    onClick={() => goToSection('queue', { queueFilter: 'all_pending' })}
                    data-testid="overview-inline-review-pending"
                  >
                    <AlertTriangle size={14} />
                    {isArabic ? 'مراجعة الإعلانات المعلّقة' : 'Review Pending Ads'}
                    <span className="overview-inline-count">{stats.listings?.ads?.pending || 0}</span>
                  </button>
                )}
                <button
                  type="button"
                  className="overview-inline-btn"
                  onClick={() => goToSection('queue', { queueFilter: 'approved' })}
                  data-testid="overview-inline-browse-ads"
                >
                  <ShoppingBag size={14} /> {isArabic ? 'تصفّح الإعلانات' : 'Browse Ads'}
                </button>
              </div>
            </div>
            
            {/* Books Stats */}
            <div className="overview-card">
              <h3><BookOpen size={18} /> {t('admin.booksStats')}</h3>
              <div className="overview-grid">
                <div className="overview-stat">
                  <span className="value">{stats.listings?.books?.approved || 0}</span>
                  <span className="label">{t('admin.approved')}</span>
                </div>
                <div className="overview-stat pending">
                  <span className="value">{stats.listings?.books?.pending || 0}</span>
                  <span className="label">{t('admin.pending')}</span>
                </div>
              </div>
            </div>
            
            {/* Reports — Total + breakdown grid */}
            <div className="overview-card">
              <h3><AlertTriangle size={18} /> {t('admin.reportsStats')}</h3>
              <div className="overview-total" data-testid="overview-reports-total">
                <span className="overview-total-label">{t('admin.totalReports')}</span>
                <span className="overview-total-value">{stats.reports?.total || 0}</span>
              </div>
              <div className="overview-grid">
                <button type="button" className="overview-stat overview-stat-btn pending" data-testid="overview-stat-reports-pending" onClick={() => goToSection('reports')}>
                  <span className="value">{stats.reports?.pending || 0}</span>
                  <span className="label">{t('admin.pendingReports')}</span>
                </button>
                <div className="overview-stat approved">
                  <span className="value">{stats.reports?.valid || 0}</span>
                  <span className="label">{isArabic ? 'صحيح' : 'Valid'}</span>
                </div>
                <div className="overview-stat">
                  <span className="value">{stats.reports?.invalid || 0}</span>
                  <span className="label">{isArabic ? 'غير صحيح' : 'Invalid'}</span>
                </div>
                <div className="overview-stat">
                  <span className="value">{stats.reports?.dismissed || 0}</span>
                  <span className="label">{isArabic ? 'مرفوض' : 'Dismissed'}</span>
                </div>
              </div>
              {/* iter283 — surface test/seed reports separately so an
                  admin can tell whether the "1 pending report" on live
                  is operational or a leftover test fixture. */}
              {(stats.reports?.test_seed || 0) > 0 && (
                <div
                  className="overview-card-actions"
                  data-testid="overview-reports-test-seed"
                  style={{ fontSize: 12, color: 'var(--admin-text-dim, #6b7280)', marginTop: 6 }}
                >
                  <span className="admin-pill gray">
                    {isArabic
                      ? `بلاغات اختبار/بذرة: ${stats.reports.test_seed}`
                      : `Test/Seed reports: ${stats.reports.test_seed}`}
                  </span>
                </div>
              )}
              {/* Contextual actions next to the Reports data */}
              {(stats.reports?.pending || 0) > 0 && (
                <div className="overview-card-actions" data-testid="overview-actions-reports">
                  <button
                    type="button"
                    className="overview-inline-btn primary"
                    onClick={() => goToSection('reports')}
                    data-testid="overview-inline-review-reports"
                  >
                    <AlertTriangle size={14} />
                    {isArabic ? 'مراجعة البلاغات' : 'Review Reports'}
                    <span className="overview-inline-count">{stats.reports?.pending || 0}</span>
                  </button>
                </div>
              )}
            </div>
            {/* iter286 — Legacy Image Repair Queue card. Surfaced
                SEPARATELY from Total Ads so the headline KPI matches
                the real marketplace inventory and isn't inflated by
                legacy cleanup rows from iter278. Operator can dive
                into the queue here via the cleanup tool. */}
            {(stats.listings?.ads?.legacy_repair_queue || stats.listings?.ads?.needs_photo_reupload || 0) > 0 && (
              <div className="overview-card" data-testid="overview-legacy-repair-queue">
                <h3>
                  <AlertTriangle size={18} />
                  {isArabic ? 'طابور إصلاح الصور القديمة' : 'Legacy Image Repair Queue'}
                </h3>
                <div className="overview-total">
                  <span className="overview-total-label">
                    {isArabic ? 'إعلانات قديمة تحتاج إعادة رفع الصورة' : 'Legacy ads awaiting photo re-upload'}
                  </span>
                  <span className="overview-total-value" data-testid="overview-legacy-repair-count">
                    {stats.listings?.ads?.legacy_repair_queue ?? stats.listings?.ads?.needs_photo_reupload ?? 0}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: 'var(--admin-text-dim, #6b7280)', marginTop: 4 }}>
                  · {isArabic
                      ? 'هذه إعلانات قديمة تأثّرت بمشكلة قرص مؤقّتة قبل ترحيل Cloudinary. ليست جزءاً من إجمالي الإعلانات.'
                      : 'These are legacy ads whose image files were lost in an ephemeral-disk wipe before the Cloudinary migration. NOT included in Total Ads.'}
                </div>
                <div className="overview-card-actions">
                  <button
                    type="button"
                    className="overview-inline-btn"
                    onClick={() => goToSection('queue', { queueFilter: 'needs_photo_reupload' })}
                    data-testid="overview-inline-browse-legacy"
                  >
                    <AlertTriangle size={14} /> {isArabic ? 'تصفح القائمة' : 'Browse Queue'}
                  </button>
                </div>
              </div>
            )}
            
            {/* iter286 — Posting Limits card. Renamed from "Daily Posting
                Policy" so the operator can't confuse the per-tier caps
                for live statistics. The numbers below are POLICY LIMITS,
                not current ad counts. */}
            {stats.posting_policy && (
              <div className="overview-card" data-testid="overview-posting-limits">
                <h3><AlertTriangle size={18} /> {isArabic ? 'حدود النشر اليومية' : 'Daily Posting Limits'}</h3>
                <div style={{ fontSize: 12, color: 'var(--admin-text-dim, #6b7280)', marginBottom: 8 }}>
                  {isArabic
                    ? 'هذه حدود نشر يومية لكل فئة مستخدم، وليست إحصائيات إعلانات حالية.'
                    : 'These are daily posting limits per user tier — not current ad counts.'}
                </div>
                <div className="overview-grid">
                  <div className="overview-stat">
                    <span className="value">{stats.posting_policy.new}</span>
                    <span className="label">{isArabic ? 'جديد / غير موثّق' : 'New / Unverified'}</span>
                  </div>
                  <div className="overview-stat">
                    <span className="value">{stats.posting_policy.trusted}</span>
                    <span className="label">{isArabic ? 'موثوق' : 'Trusted'}</span>
                  </div>
                  <div className="overview-stat approved">
                    <span className="value">{stats.posting_policy.verified_seller}</span>
                    <span className="label">{isArabic ? 'بائع موثّق' : 'Verified Seller'}</span>
                  </div>
                  <div className="overview-stat">
                    <span className="value">{stats.posting_policy.admin}</span>
                    <span className="label">{isArabic ? 'مشرف' : 'Admin'}</span>
                  </div>
                  <div className="overview-stat">
                    <span className="value" style={{ fontSize: 14 }}>{isArabic ? 'غير محدود' : 'Unlimited'}</span>
                    <span className="label">{isArabic ? 'المالك' : 'Owner'}</span>
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--admin-text-dim, #6b7280)', marginTop: 8 }}>
                  · {isArabic
                      ? `نافذة متحركة 24 ساعة. تُحسب فقط الإعلانات المنشورة (نشط/قيد المراجعة/مُباع). المسودات لا تُحسب.`
                      : `Rolling 24h window. Only successfully published ads count (active / pending_review / sold). Drafts do NOT count.`}
                  {' '}
                  · {isArabic
                      ? `المستخدمون الذين لديهم بلاغات → يُقسم الحد على ${stats.posting_policy.strike_divisor}.`
                      : `Users with strikes → tier limit divided by ${stats.posting_policy.strike_divisor}.`}
                </div>
              </div>
            )}
            
            {/* Payments — Total + breakdown grid */}
            <div className="overview-card">
              <h3><CreditCard size={18} /> {isArabic ? 'إحصائيات الدفع' : 'Payment Statistics'}</h3>
              <div className="overview-total" data-testid="overview-payments-total">
                <span className="overview-total-label">{isArabic ? 'إجمالي طلبات الدفع' : 'Total Payment Requests'}</span>
                <span className="overview-total-value">{stats.payments?.total || 0}</span>
              </div>
              <div className="overview-grid">
                <button type="button" className="overview-stat overview-stat-btn pending" data-testid="overview-stat-payments-pending" onClick={() => goToSection('payments')}>
                  <span className="value">{stats.payments?.pending || 0}</span>
                  <span className="label">{t('admin.pending')}</span>
                </button>
                <div className="overview-stat approved">
                  <span className="value">{stats.payments?.approved || 0}</span>
                  <span className="label">{t('admin.approved')}</span>
                </div>
                <div className="overview-stat rejected">
                  <span className="value">{stats.payments?.rejected || 0}</span>
                  <span className="label">{t('admin.rejected')}</span>
                </div>
              </div>
              {/* Contextual actions next to the Payments data */}
              {(stats.payments?.pending || 0) > 0 && (
                <div className="overview-card-actions" data-testid="overview-actions-payments">
                  <button
                    type="button"
                    className="overview-inline-btn primary"
                    onClick={() => goToSection('payments')}
                    data-testid="overview-inline-review-payments"
                  >
                    <CreditCard size={14} />
                    {isArabic ? 'مراجعة المدفوعات' : 'Review Payments'}
                    <span className="overview-inline-count">{stats.payments?.pending || 0}</span>
                  </button>
                </div>
              )}
            </div>
            
            {/* Quick Actions */}
            <div className="overview-actions">
              <button className="action-btn" onClick={() => setActiveTab('queue')} data-testid="quick-review-ads">
                <ShoppingBag size={18} />
                {t('admin.reviewAds')} ({queueTotal})
              </button>
              <button className="action-btn" onClick={() => setActiveTab('payments')} data-testid="quick-review-payments">
                <CreditCard size={18} />
                {t('admin.reviewPayments')} ({paymentsTotal})
              </button>
              <button className="action-btn" onClick={() => setActiveTab('reports')} data-testid="quick-review-reports">
                <AlertTriangle size={18} />
                {t('admin.reviewReports')} ({reportsTotal})
              </button>
            </div>
          </div>
        )}
        {/* Review Queue Tab */}
        {activeTab === 'queue' && (
          <div className="queue-section">
            <h2 className="section-title">
              {t('admin.pendingAds')}
              <span className="count">({queueTotal})</span>
            </h2>
            
            {/* Enhanced Filters */}
            <div className="queue-filters" data-testid="queue-filters">
              <div className="filter-group">
                <label>{isArabic ? 'تصفية حسب الحالة' : 'Filter by Status'}</label>
                <SelectField
                  value={queueFilter}
                  onChange={(val) => setQueueFilter(val)}
                  options={[
                    { value: 'all_pending', label: isArabic ? 'كل المعلق' : 'All Pending' },
                    { value: 'held', label: isArabic ? 'قيد المراجعة' : 'Held for Review' },
                    { value: 'limited', label: isArabic ? 'محدود' : 'Limited' },
                    { value: 'hidden', label: isArabic ? 'مخفي' : 'Hidden' },
                    { value: 'approved', label: isArabic ? 'موافق عليه' : 'Approved' },
                    { value: 'rejected', label: isArabic ? 'مرفوض' : 'Rejected' },
                    { value: 'all', label: isArabic ? 'جميع الإعلانات' : 'All Ads' },
                  ]}
                  data-testid="queue-filter-status"
                />
              </div>
              <div className="filter-group">
                <label>{isArabic ? 'ترتيب حسب' : 'Sort by'}</label>
                <SelectField
                  value={queueSortBy}
                  onChange={(val) => setQueueSortBy(val)}
                  options={[
                    { value: 'risk_score', label: isArabic ? 'درجة الخطر' : 'Risk Score' },
                    { value: 'created_at', label: isArabic ? 'تاريخ الإنشاء' : 'Date Created' },
                    { value: 'report_count', label: isArabic ? 'عدد البلاغات' : 'Report Count' },
                  ]}
                  data-testid="queue-sort-by"
                />
              </div>
              <div className="filter-group">
                <label>{isArabic ? 'الترتيب' : 'Order'}</label>
                <SelectField
                  value={queueSortOrder}
                  onChange={(val) => setQueueSortOrder(val)}
                  options={[
                    { value: 'desc', label: isArabic ? 'تنازلي' : 'Descending' },
                    { value: 'asc', label: isArabic ? 'تصاعدي' : 'Ascending' },
                  ]}
                  data-testid="queue-sort-order"
                />
              </div>
              <button
                className="refresh-btn"
                onClick={async () => {
                  if (refreshing) return;
                  setRefreshing(true);
                  try {
                    await Promise.all([fetchQueue(), fetchStats()]);
                    toast.success(isArabic ? 'تم التحديث' : 'Refreshed');
                  } catch (e) {
                    toast.error((isArabic ? 'فشل التحديث: ' : 'Refresh failed: ') + (e?.message || 'network'));
                  } finally {
                    setRefreshing(false);
                  }
                }}
                disabled={refreshing}
                aria-busy={refreshing}
                data-testid="refresh-queue"
              >
                <RefreshCw size={16} className={refreshing ? 'spin' : ''} />
                {isArabic ? 'تحديث' : 'Refresh'}
              </button>
            </div>
            
            {/* Risk Distribution Summary */}
            {moderationStats && (
              <div className="risk-summary" data-testid="risk-summary">
                <div className="risk-stat high">
                  <AlertTriangle size={18} />
                  <span className="risk-count" data-testid="risk-high-count">{moderationStats.high_risk || moderationStats.risk_distribution?.high || 0}</span>
                  <span className="risk-label">{isArabic ? 'خطر عالي' : 'High Risk'}</span>
                </div>
                <div className="risk-stat medium">
                  <AlertCircle size={18} />
                  <span className="risk-count" data-testid="risk-medium-count">{moderationStats.medium_risk || moderationStats.risk_distribution?.medium || 0}</span>
                  <span className="risk-label">{isArabic ? 'خطر متوسط' : 'Medium Risk'}</span>
                </div>
                <div className="risk-stat low">
                  <CheckCircle size={18} />
                  <span className="risk-count" data-testid="risk-low-count">{moderationStats.low_risk || moderationStats.risk_distribution?.low || 0}</span>
                  <span className="risk-label">{isArabic ? 'خطر منخفض' : 'Low Risk'}</span>
                </div>
              </div>
            )}
            {/* iter325 — Parity proof. high + medium + low MUST equal
                total_reviewed_real_ads (real, actionable ads only —
                test/seed/legacy/deleted are out). If they disagree the
                line renders an amber warning so the operator never has
                to second-guess what these buckets cover. */}
            {moderationStats && typeof moderationStats.total_reviewed_real_ads === 'number' && (() => {
              const h = moderationStats.high_risk || 0;
              const m = moderationStats.medium_risk || 0;
              const l = moderationStats.low_risk || 0;
              const total = moderationStats.total_reviewed_real_ads;
              const match = (h + m + l) === total;
              return (
                <p
                  data-testid="risk-parity-proof"
                  data-match={String(match)}
                  style={{
                    margin: '8px 0 16px', fontSize: 12,
                    color: match ? 'var(--admin-text-dim)' : '#92400e',
                  }}
                >
                  {match ? '✓ ' : '⚠ '}
                  {isArabic
                    ? `${h} + ${m} + ${l} = ${h + m + l} ${match ? '==' : '!='} ${total} إعلان فعلي قيد المراجعة (بدون اختبار/بذرة/قديم)`
                    : `${h} + ${m} + ${l} = ${h + m + l} ${match ? '==' : '!='} ${total} actionable reviewed ads (excludes test/seed/legacy)`}
                </p>
              );
            })()}
            
            {queueAds.length === 0 ? (
              <div className="empty-state">
                <CheckCircle size={48} />
                <p>{t('admin.allClear')}</p>
              </div>
            ) : (
              <div className="queue-list">
                {queueAds.map(ad => {
                  const statusBadge = getStatusBadge(ad.status);
                  const StatusIcon = statusBadge.icon;
                  const riskLevel = getRiskLevel(ad.risk_score || 0);
                  const riskFactors = ad.risk_factors || ad.moderation_flags || [];
                  return (
                    <div key={ad.ad_id} className="queue-item" data-testid={`queue-item-${ad.ad_id}`}>
                      <div className="queue-item-main">
                        {/* Risk Score Badge — 0–100 scale; level tints colour */}
                        <div 
                          className={`risk-score-badge ${riskLevel.level}`}
                          style={{ backgroundColor: riskLevel.color }}
                          title={`${isArabic ? 'درجة الخطر' : 'Risk Score'} ${ad.risk_score || 0}/100 — ${riskLevel.label} ${isArabic ? '' : 'risk'}`.trim()}
                          data-testid={`risk-badge-${ad.ad_id}`}
                        >
                          <span className="risk-number">{ad.risk_score || 0}/100</span>
                          <span className="risk-label">
                            {isArabic ? `خطر ${riskLevel.label}` : `${riskLevel.label} risk`}
                          </span>
                        </div>
                        
                        {/* Thumbnail */}
                        <div className="queue-item-thumb" onClick={() => fetchAdDetails(ad)}>
                          <img 
                            src={getAdCoverImage(ad) || '/placeholder.svg'} 
                            alt={ad.title}
                            onError={(e) => { e.target.src = '/placeholder.svg'; }}
                          />
                        </div>
                        
                        <div className="queue-item-content">
                          <div className="queue-item-header">
                            <h3 className="queue-item-title">{ad.title}</h3>
                            <span className={`status-badge ${statusBadge.color}`}>
                              <StatusIcon size={14} />
                              {statusBadge.label}
                            </span>
                          </div>
                          <p className="queue-item-desc">{ad.description?.slice(0, 100)}...</p>
                          <div className="queue-item-meta">
                            <span className="owner" title={ad.user_email || ad.user_name || ad.user_id || ''}>{t('admin.adOwner')}: {ad.user_email || ad.user_name || ad.user_id?.slice(0, 8)}</span>
                            {ad.user_trust !== undefined && (
                              <span title={isArabic ? 'ثقة المستخدم' : 'User Trust'}>
                                👤 {ad.user_trust}
                              </span>
                            )}
                            <span>{t('admin.reportsCount')}: {ad.report_count || 0}</span>
                            {ad.weighted_report_score > 0 && (
                              <span title={isArabic ? 'درجة البلاغات المرجحة' : 'Weighted Report Score'}>
                                📊 {ad.weighted_report_score}
                              </span>
                            )}
                            <span>{t('cat.' + ad.category) || ad.category}</span>
                          </div>
                          
                          {/* Risk Factors */}
                          {riskFactors.length > 0 && (
                            <div className="risk-factors" data-testid={`factors-${ad.ad_id}`}>
                              {riskFactors.slice(0, 5).map((factor, idx) => {
                                const factorCode = typeof factor === 'string' ? factor : factor.code;
                                const factorWeight = typeof factor === 'object' ? factor.weight : null;
                                const factorCategory = typeof factor === 'object' ? factor.category : 'general';
                                return (
                                  <span 
                                    key={idx} 
                                    className={`risk-factor ${factorCategory === 'prohibited' ? 'prohibited' : factorCategory === 'suspicious' ? 'suspicious' : 'general'}`}
                                    title={factorWeight ? `+${factorWeight} ${isArabic ? 'نقطة' : 'points'}` : ''}
                                  >
                                    {getRiskFactorLabel(factorCode)}
                                    {factorWeight && <span className="factor-weight">+{factorWeight}</span>}
                                  </span>
                                );
                              })}
                              {riskFactors.length > 5 && (
                                <span className="risk-factor more">+{riskFactors.length - 5}</span>
                              )}
                            </div>
                          )}
                          
                          {/* Auto-hide Warning */}
                          {ad.should_auto_hide && (
                            <div className="auto-hide-warning">
                              <AlertTriangle size={14} />
                              {isArabic ? 'سيتم إخفاء هذا الإعلان تلقائياً بسبب البلاغات' : 'This ad will be auto-hidden due to reports'}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="queue-item-actions">
                        <button 
                          className="action-btn approve"
                          onClick={() => askConfirmEnhanced(ad.ad_id, 'APPROVE')}
                          data-testid={`approve-${ad.ad_id}`}
                        >
                          <CheckCircle size={16} />
                          {t('admin.approve')}
                        </button>
                        <button 
                          className="action-btn limit"
                          onClick={() => askConfirmEnhanced(ad.ad_id, ad.visibility === 'limited' ? 'UNLIMIT' : 'LIMIT')}
                          data-testid={`limit-${ad.ad_id}`}
                        >
                          <AlertTriangle size={16} />
                          {ad.visibility === 'limited' ? (isArabic ? 'إلغاء التقييد' : 'Unlimit') : t('admin.limit')}
                        </button>
                        <button 
                          className="action-btn hide"
                          onClick={() => askConfirmEnhanced(ad.ad_id, ad.status === 'hidden' ? 'UNHIDE' : 'HIDE')}
                          data-testid={`hide-${ad.ad_id}`}
                        >
                          <EyeOff size={16} />
                          {ad.status === 'hidden' ? (isArabic ? 'إظهار' : 'Unhide') : (isArabic ? 'إخفاء' : 'Hide')}
                        </button>
                        <button 
                          className="action-btn reject"
                          onClick={() => openRejectModal(ad)}
                          data-testid={`reject-${ad.ad_id}`}
                        >
                          <XCircle size={16} />
                          {t('admin.reject')}
                        </button>
                        <button 
                          className="action-btn view"
                          onClick={() => fetchAdDetails(ad)}
                          data-testid={`view-${ad.ad_id}`}
                        >
                          <Eye size={16} />
                          {t('admin.viewDetails')}
                        </button>
                        {ad.user_id && (
                          <button 
                            className="action-btn ban"
                            onClick={() => openBanModal({ user_id: ad.user_id, email: ad.user_email, name: ad.user_name })}
                            data-testid={`ban-user-${ad.ad_id}`}
                            title={isArabic ? 'حظر المستخدم' : 'Ban User'}
                          >
                            <Ban size={16} />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Books Tab */}
        {activeTab === 'books' && (
          <div className="queue-section">
            <h2 className="section-title">
              {t('admin.pendingBooks')}
              <span className="count">({queueBooksTotal})</span>
            </h2>
            
            {queueBooks.length === 0 ? (
              <div className="empty-state">
                <CheckCircle size={48} />
                <p>{t('admin.allClear')}</p>
              </div>
            ) : (
              <div className="queue-list">
                {queueBooks.map(book => (
                  <div key={book.book_id} className="queue-item" data-testid={`book-item-${book.book_id}`}>
                    <div className="queue-item-main">
                      <div className="queue-item-thumb">
                        <img 
                          src={getImageUrl(book.images?.[0])} 
                          alt={book.title}
                          onError={(e) => { e.target.src = '/placeholder.svg'; }}
                        />
                      </div>
                      
                      <div className="queue-item-content">
                        <div className="queue-item-header">
                          <h3 className="queue-item-title">{book.title}</h3>
                          <span className={`status-badge ${book.status === 'available' ? 'green' : 'yellow'}`}>
                            {book.status}
                          </span>
                        </div>
                        <p className="queue-item-desc">
                          {book.author && `${t('author')}: ${book.author}`}
                          {book.description && ` - ${book.description.slice(0, 100)}...`}
                        </p>
                        <div className="queue-item-meta">
                          <span>{t('admin.owner')}: {book.user_email || book.user_id?.slice(0, 8)}</span>
                          <span>{t('mode')}: {book.mode}</span>
                          <span>{t('condition')}: {book.condition}</span>
                          <span>{formatDate(book.created_at)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="queue-item-actions">
                      <button 
                        className="action-btn approve"
                        onClick={() => askConfirm('approve', 'books', book.book_id)}
                        data-testid={`approve-book-${book.book_id}`}
                      >
                        <CheckCircle size={16} />
                        {t('admin.approve')}
                      </button>
                      <button 
                        className="action-btn reject"
                        onClick={() => askConfirm('reject', 'books', book.book_id)}
                        data-testid={`reject-book-${book.book_id}`}
                      >
                        <XCircle size={16} />
                        {t('admin.reject')}
                      </button>
                      <button 
                        className="action-btn secondary"
                        onClick={() => askConfirm('hide', 'books', book.book_id, { isHidden: !book.is_hidden })}
                        data-testid={`hide-book-${book.book_id}`}
                      >
                        {book.is_hidden ? <Eye size={16} /> : <EyeOff size={16} />}
                        {book.is_hidden ? t('admin.unhide') : t('admin.hide')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Payments Tab */}
        {activeTab === 'payments' && (
          <div className="queue-section">
            <h2 className="section-title">
              {t('admin.pendingPayments')}
              <span className="count">({paymentsTotal})</span>
            </h2>
            
            {/* Payment Stats Summary */}
            <div className="payment-stats-summary" style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <div className="payment-stat-card" style={{ 
                background: '#fef3c7', 
                padding: '1rem 1.5rem', 
                borderRadius: '12px',
                flex: '1',
                minWidth: '150px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#92400e' }}>{paymentsStats.pending}</span>
                <span style={{ display: 'block', fontSize: '0.875rem', color: '#78350f' }}>{t('admin.pending')}</span>
              </div>
              <div className="payment-stat-card" style={{ 
                background: '#d1fae5', 
                padding: '1rem 1.5rem', 
                borderRadius: '12px',
                flex: '1',
                minWidth: '150px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#134725' }}>{paymentsStats.approved}</span>
                <span style={{ display: 'block', fontSize: '0.875rem', color: '#064e3b' }}>{t('admin.approved')}</span>
              </div>
              <div className="payment-stat-card" style={{ 
                background: '#fee2e2', 
                padding: '1rem 1.5rem', 
                borderRadius: '12px',
                flex: '1',
                minWidth: '150px'
              }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#991b1b' }}>{paymentsStats.rejected}</span>
                <span style={{ display: 'block', fontSize: '0.875rem', color: '#7f1d1d' }}>{t('admin.rejected')}</span>
              </div>
            </div>
            
            {payments.length === 0 ? (
              <div className="empty-state">
                <CheckCircle size={48} />
                <p>{t('admin.noPaymentsPending')}</p>
              </div>
            ) : (
              <div className="queue-list">
                {payments.map(payment => (
                  <div key={payment.request_id} className="queue-item" data-testid={`payment-item-${payment.request_id}`}>
                    <div className="queue-item-main">
                      <div className="queue-item-icon" style={{ 
                        width: '64px', 
                        height: '64px', 
                        background: '#e0f2fe',
                        borderRadius: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <Receipt size={32} style={{ color: '#0284c7' }} />
                      </div>
                      
                      <div className="queue-item-content">
                        <div className="queue-item-header">
                          <h3 className="queue-item-title">{payment.feature || 'CV Pro PDF'}</h3>
                          <span className={`status-badge ${payment.payment_status === 'pending' ? 'yellow' : payment.payment_status === 'approved' ? 'green' : 'red'}`}>
                            {payment.payment_status || 'pending'}
                          </span>
                        </div>
                        <p className="queue-item-desc">
                          <strong>{t('admin.amount')}:</strong> {formatCurrency(payment.amount_sdg, 'SDG')}
                        </p>
                        <div className="queue-item-meta">
                          <span>{t('admin.owner')}: {payment.user_email || payment.user_name || payment.user_id?.slice(0, 12)}</span>
                          <span>{t('admin.method')}: Bankak Manual</span>
                          <span>{formatDate(payment.created_at)}</span>
                        </div>
                        {payment.receipt_image_url && (
                          <div style={{ marginTop: '0.5rem' }}>
                            <a 
                              href={getImageUrl(payment.receipt_image_url)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              style={{ 
                                color: '#0284c7', 
                                fontSize: '0.875rem',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <Image size={14} />
                              {t('admin.viewReceipt') || 'View Receipt'}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="queue-item-actions">
                      <button 
                        className="action-btn approve"
                        onClick={() => handleApprovePayment(payment.request_id)}
                        data-testid={`approve-payment-${payment.request_id}`}
                      >
                        <CheckCircle size={16} />
                        {t('admin.approve') || 'Approve'}
                      </button>
                      <button 
                        className="action-btn reject"
                        onClick={() => openPaymentRejectModal(payment)}
                        data-testid={`reject-payment-${payment.request_id}`}
                      >
                        <XCircle size={16} />
                        {t('admin.reject') || 'Reject'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="reports-section">
            <h2 className="section-title">
              {t('admin.reports')}
              <span className="count">({reportsTotal})</span>
            </h2>
            
            {reports.length === 0 ? (
              <div className="empty-state">
                <CheckCircle size={48} />
                <p>{t('admin.allReportsResolved')}</p>
              </div>
            ) : (
              <div className="reports-list">
                {reports.map(report => (
                  <div key={report.report_id} className="report-item" data-testid={`report-${report.report_id}`}>
                    <div className="report-item-main">
                      <div className="report-item-header">
                        <h3>{report.ad?.title || t('admin.noReports')}</h3>
                        <span className={`reason-badge ${report.reason}`}>
                          {report.reason}
                        </span>
                      </div>
                      <p className="report-note">{report.note || '-'}</p>
                      <div className="report-meta">
                        <span>{t('admin.reporter')}: {report.reporter?.email || '-'}</span>
                        <span>{t('admin.adOwner')}: {report.ad?.user_email || '-'}</span>
                        <span>{t('admin.reportStatus')}: {report.status || 'pending'}</span>
                        <span>{formatDate(report.created_at)}</span>
                      </div>
                    </div>
                    <div className="report-item-actions">
                      <button 
                        className="action-btn approve"
                        onClick={() => handleReportAction(report.report_id, 'valid')}
                        disabled={report.status === 'validated'}
                        data-testid={`report-valid-${report.report_id}`}
                      >
                        <CheckCircle size={16} />
                        {t('admin.markValid')}
                      </button>
                      <button 
                        className="action-btn reject"
                        onClick={() => handleReportAction(report.report_id, 'invalid')}
                        disabled={report.status === 'invalidated'}
                        data-testid={`report-invalid-${report.report_id}`}
                      >
                        <XCircle size={16} />
                        {t('admin.markInvalid')}
                      </button>
                      <button 
                        className="action-btn"
                        onClick={() => handleReportAction(report.report_id, 'dismiss')}
                        disabled={report.status === 'dismissed'}
                        data-testid={`report-dismiss-${report.report_id}`}
                      >
                        {t('admin.dismiss')}
                      </button>
                      {report.ad?.ad_id && (
                        <button 
                          className="action-btn view"
                          onClick={() => navigate(`/ad/${report.ad.ad_id}`)}
                        >
                          <ExternalLink size={16} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="users-section">
            <div className="users-header">
              <h2 className="section-title">
                {showBannedOnly ? t('admin.bannedUsersList') : t('admin.users')}
                <span className="count">({usersTotal})</span>
              </h2>
              
              <div className="users-controls" onClick={(e) => e.stopPropagation()}>
                {/* Search — native input keeps focus while typing (debounced fetch) */}
                <label className="search-input-wrapper" htmlFor="user-search-input" onClick={(e) => e.stopPropagation()}>
                  <Search size={18} aria-hidden="true" />
                  <input
                    id="user-search-input"
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => { if (e.key === 'Escape') setUserSearch(''); }}
                    placeholder={t('admin.searchUsers')}
                    className="user-search-input"
                    data-testid="user-search"
                    autoComplete="off"
                    spellCheck="false"
                  />
                </label>
                
                {/* Sort Controls */}
                <div className="sort-controls">
                  <select 
                    value={userSort} 
                    onChange={(e) => setUserSort(e.target.value)}
                    className="sort-select"
                  >
                    <option value="trust_score">{t('admin.trustScore')}</option>
                    <option value="created_at">{t('admin.joinDate')}</option>
                  </select>
                  <button 
                    className="sort-order-btn"
                    onClick={() => setUserSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  >
                    {userSortOrder === 'asc' ? '↑' : '↓'}
                  </button>
                </div>
              </div>
            </div>
            
            {users.length === 0 ? (
              <div className="empty-state">
                <Users size={48} />
                <p>{showBannedOnly ? t('admin.noBannedUsers') : t('noResults')}</p>
              </div>
            ) : (
              <div className="users-list">
                {users.map(u => (
                  <div key={u.user_id} className="user-item" data-testid={`user-${u.user_id}`}>
                    <div className="user-item-main">
                      <div className="user-avatar">
                        {u.avatar ? (
                          <img src={getImageUrl(u.avatar)} alt={u.name} onError={(e) => { e.target.style.display = 'none'; }} />
                        ) : (
                          <span>{u.name?.charAt(0)?.toUpperCase() || '?'}</span>
                        )}
                      </div>
                      <div className="user-info">
                        <h3 className="user-name">
                          {u.name || '-'}
                          {u.is_banned && <span className="banned-badge">{t('admin.bannedBadge')}</span>}
                          {u.is_admin && <span className="admin-badge">{t('admin.adminBadge')}</span>}
                        </h3>
                        <p className="user-email">{u.email}</p>
                        <div className="user-stats">
                          <span>{t('admin.adsCount')}: {u.ad_count || 0}</span>
                          <span>{t('admin.rejectedCount')}: {u.rejected_count || 0}</span>
                          <span>{t('admin.userReports')}: {u.report_count || 0}</span>
                        </div>
                      </div>
                      <div
                        className={`trust-score ${getTrustColor(u.trust_score)}`}
                        title={`${t('admin.trustScore')} ${u.trust_score ?? 100}/100`}
                        data-testid={`trust-${u.user_id}`}
                      >
                        <span className="trust-label">{t('admin.trustScore')}</span>
                        <span className="trust-value">{u.trust_score ?? 100}/100</span>
                      </div>
                    </div>
                    <div className="user-item-actions">
                      {!u.is_banned ? (
                        <button 
                          className="action-btn reject"
                          onClick={() => { setUserToBan(u); setBanConfirmOpen(true); }}
                          disabled={u.is_admin}
                          data-testid={`ban-${u.user_id}`}
                        >
                          <Ban size={16} />
                          {t('admin.ban')}
                        </button>
                      ) : (
                        <button 
                          className="action-btn approve"
                          onClick={() => askConfirmUserAction(u.user_id, 'unban', u.name || u.email)}
                          data-testid={`unban-${u.user_id}`}
                        >
                          <Unlock size={16} />
                          {t('admin.unban')}
                        </button>
                      )}
                      <button 
                        className="action-btn"
                        onClick={() => askConfirmUserAction(u.user_id, 'reset_trust', u.name || u.email)}
                        data-testid={`reset-${u.user_id}`}
                      >
                        <RefreshCw size={16} />
                        {t('admin.resetTrust')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Blocklist Tab */}
        {activeTab === 'blocklist' && (
          <div className="blocklist-section">
            <h2 className="section-title">{t('admin.blockedWords')}</h2>
            
            <div className="add-word-form">
              <input
                type="text"
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                placeholder={t('admin.newWord')}
                className="word-input"
                data-testid="new-word-input"
              />
              <select 
                value={newWordType} 
                onChange={(e) => setNewWordType(e.target.value)}
                className="type-select"
              >
                <option value="soft">{t('admin.softLabel')}</option>
                <option value="hard">{t('admin.hardLabel')}</option>
              </select>
              <button className="add-btn" onClick={handleAddToBlocklist} data-testid="add-word-btn">
                <Plus size={18} />
                {t('admin.addWord')}
              </button>
            </div>

            <div className="blocklist-groups">
              <div className="blocklist-group">
                <h3 className="group-title red">{t('admin.hardBlock')}</h3>
                <div className="word-chips">
                  {blocklist.hard?.length === 0 ? (
                    <span className="empty-chips">-</span>
                  ) : (
                    blocklist.hard?.map((word, idx) => (
                      <span key={idx} className="word-chip hard">{word}</span>
                    ))
                  )}
                </div>
              </div>
              
              <div className="blocklist-group">
                <h3 className="group-title yellow">{t('admin.softBlock')}</h3>
                <div className="word-chips">
                  {blocklist.soft?.length === 0 ? (
                    <span className="empty-chips">-</span>
                  ) : (
                    blocklist.soft?.map((word, idx) => (
                      <span key={idx} className="word-chip soft">{word}</span>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Ad Details Drawer */}
      <Sheet open={!!selectedAdForDetails} onOpenChange={() => setSelectedAdForDetails(null)}>
        <SheetContent side={isArabic ? 'left' : 'right'} className="ad-details-drawer">
          <SheetHeader>
            <SheetTitle>{t('admin.adDetails')}</SheetTitle>
          </SheetHeader>
          
          {adDetailsLoading ? (
            <div className="drawer-loading">
              <RefreshCw className="animate-spin" size={24} />
            </div>
          ) : selectedAdForDetails && (
            <div className="ad-details-content">
              {/* Images Carousel */}
              <div className="ad-images-section">
                <h4><Image size={18} /> {t('admin.images')}</h4>
                {selectedAdForDetails.images?.length > 0 ? (
                  <div className="ad-carousel">
                    <div className="carousel-main">
                      <img 
                        src={getImageUrl(selectedAdForDetails.images[currentImageIndex])} 
                        alt={selectedAdForDetails.title}
                        onError={(e) => { e.target.src = '/placeholder.svg'; }}
                      />
                      {selectedAdForDetails.images.length > 1 && (
                        <>
                          <button 
                            className="carousel-btn prev"
                            onClick={() => setCurrentImageIndex(prev => 
                              prev === 0 ? selectedAdForDetails.images.length - 1 : prev - 1
                            )}
                          >
                            <ChevronLeft size={20} />
                          </button>
                          <button 
                            className="carousel-btn next"
                            onClick={() => setCurrentImageIndex(prev => 
                              prev === selectedAdForDetails.images.length - 1 ? 0 : prev + 1
                            )}
                          >
                            <ChevronRight size={20} />
                          </button>
                        </>
                      )}
                    </div>
                    {selectedAdForDetails.images.length > 1 && (
                      <div className="carousel-thumbs">
                        {selectedAdForDetails.images.map((img, idx) => (
                          <button 
                            key={idx}
                            className={`thumb ${idx === currentImageIndex ? 'active' : ''}`}
                            onClick={() => setCurrentImageIndex(idx)}
                          >
                            <img src={getImageUrl(img)} alt="" onError={(e) => { e.target.src = '/placeholder.svg'; }} />
                          </button>
                        ))}
                      </div>
                    )}
                    <div className="carousel-counter">
                      {currentImageIndex + 1} / {selectedAdForDetails.images.length}
                    </div>
                  </div>
                ) : (
                  <div className="no-images">
                    <Image size={32} />
                    <p>No images</p>
                  </div>
                )}
              </div>

              {/* Ad Info */}
              <div className="ad-info-section">
                <h3>{selectedAdForDetails.title}</h3>
                <div className="ad-meta-row">
                  <span className={`status-badge ${getStatusBadge(selectedAdForDetails.status).color}`}>
                    {t(getStatusBadge(selectedAdForDetails.status).labelKey)}
                  </span>
                  <span>{t('admin.riskScore')}: {selectedAdForDetails.risk_score || 0}</span>
                </div>
                <p className="ad-description">{selectedAdForDetails.description}</p>
                <div className="ad-meta-list">
                  <div><strong>{t('admin.adOwner')}:</strong> {selectedAdForDetails.user_email || selectedAdForDetails.user_id}</div>
                  <div><strong>{t('cat.' + selectedAdForDetails.category)}:</strong> {selectedAdForDetails.category}</div>
                  <div><strong>{t('admin.createdAt')}:</strong> {formatDate(selectedAdForDetails.created_at)}</div>
                </div>
              </div>

              {/* Moderation History */}
              <div className="moderation-history-section">
                <h4><History size={18} /> {t('admin.moderationHistory')}</h4>
                {adModerationHistory.length === 0 ? (
                  <p className="no-data">{t('admin.noHistory')}</p>
                ) : (
                  <div className="history-list">
                    {adModerationHistory.map((item, idx) => (
                      <div key={idx} className="history-item">
                        <span className="history-action">{item.action}</span>
                        <span className="history-date">{formatDate(item.created_at)}</span>
                        {item.note && <p className="history-note">{item.note}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Reports for this ad */}
              <div className="ad-reports-section">
                <h4><MessageSquare size={18} /> {t('admin.relatedReports')}</h4>
                {adReports.length === 0 ? (
                  <p className="no-data">{t('admin.noReportsForAd')}</p>
                ) : (
                  <div className="ad-reports-list">
                    {adReports.map((report, idx) => (
                      <div key={idx} className="ad-report-item">
                        <span className="report-reason">{report.reason}</span>
                        <span className="report-reporter">{report.reporter?.email}</span>
                        <p className="report-note">{report.note}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="drawer-actions">
                <button 
                  className="action-btn approve full"
                  onClick={() => { handleAdAction(selectedAdForDetails.ad_id, 'approve'); }}
                >
                  <CheckCircle size={16} />
                  {t('admin.approve')}
                </button>
                <button 
                  className="action-btn limit full"
                  onClick={() => { handleAdAction(selectedAdForDetails.ad_id, 'limit'); }}
                >
                  <AlertTriangle size={16} />
                  {t('admin.limit')}
                </button>
                <button 
                  className="action-btn reject full"
                  onClick={() => { setSelectedAdForDetails(null); openRejectModal(selectedAdForDetails); }}
                >
                  <XCircle size={16} />
                  {t('admin.reject')}
                </button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Reject Modal with Required Reason */}
      <AlertDialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.rejectAd')}</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{adToReject?.title}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="reject-form">
            <label>{t('admin.rejectReason')} *</label>
            <textarea
              value={rejectNote}
              onChange={(e) => { setRejectNote(e.target.value); setRejectError(''); }}
              placeholder={t('admin.rejectReasonPlaceholder')}
              className={`reject-note-input ${rejectError ? 'error' : ''}`}
              rows={4}
            />
            {rejectError && <span className="reject-error">{rejectError}</span>}
          </div>
          
          <AlertDialogFooter className="modalActions">
            <AlertDialogCancel onClick={() => { setRejectModalOpen(false); setAdToReject(null); setRejectNote(''); setRejectError(''); }} className="btn btnSecondaryOrange">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleRejectWithReason} className="btn btnPrimaryOrange">
              {t('admin.confirmReject')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Ban Confirmation Dialog */}
      <AlertDialog open={banConfirmOpen} onOpenChange={setBanConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.ban')}</AlertDialogTitle>
            <AlertDialogDescription>
              {isArabic 
                ? `هل أنت متأكد من حظر المستخدم "${userToBan?.name || userToBan?.email}"؟`
                : `Are you sure you want to ban "${userToBan?.name || userToBan?.email}"?`
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="modalActions">
            <AlertDialogCancel onClick={() => { setBanConfirmOpen(false); setUserToBan(null); }} className="btn btnSecondaryOrange">
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleUserAction(userToBan?.user_id, 'ban', 'admin_action')}
              className="btn btnPrimaryOrange"
            >
              {t('admin.ban')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Payment Reject Modal with Required Reason */}
      <AlertDialog open={paymentRejectModalOpen} onOpenChange={setPaymentRejectModalOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.rejectPayment') || 'Reject Payment'}</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{paymentToReject?.feature || 'CV Pro PDF'}</strong> - {formatCurrency(paymentToReject?.amount_sdg, 'SDG')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="reject-form">
            <label>{t('admin.rejectReason') || 'Rejection Reason'} *</label>
            <textarea
              value={paymentRejectReason}
              onChange={(e) => { setPaymentRejectReason(e.target.value); setPaymentRejectError(''); }}
              placeholder={t('admin.paymentRejectReasonPlaceholder') || 'Please provide a reason for rejecting this payment (e.g., Invalid receipt, Incorrect amount)'}
              className={`reject-note-input ${paymentRejectError ? 'error' : ''}`}
              rows={4}
            />
            {paymentRejectError && <span className="reject-error">{paymentRejectError}</span>}
          </div>
          
          <AlertDialogFooter className="modalActions">
            <AlertDialogCancel 
              onClick={() => { setPaymentRejectModalOpen(false); setPaymentToReject(null); setPaymentRejectReason(''); setPaymentRejectError(''); }} 
              className="btn btnSecondaryOrange"
            >
              {t('cancel') || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handlePaymentRejectWithReason} 
              className="btn btnPrimaryOrange"
            >
              {t('admin.confirmReject') || 'Confirm Reject'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Enhanced Ban Modal with Duration */}
      <AlertDialog open={banModalOpen} onOpenChange={setBanModalOpen}>
        <AlertDialogContent className="admin-ban-dialog" data-testid="admin-ban-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isArabic ? 'حظر المستخدم' : 'Ban User'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {banTargetUser && (
                <div className="ban-user-info">
                  <p><strong>{isArabic ? 'البريد' : 'Email'}:</strong> {banTargetUser.email || banTargetUser.user_id}</p>
                  {banTargetUser.name && <p><strong>{isArabic ? 'الاسم' : 'Name'}:</strong> {banTargetUser.name}</p>}
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="ban-form">
            <div className="form-group">
              <label>{isArabic ? 'نوع الحظر' : 'Ban Type'}</label>
              <SelectField
                value={banType}
                onChange={(val) => setBanType(val)}
                options={[
                  { value: 'TEMP', label: isArabic ? 'مؤقت' : 'Temporary' },
                  { value: 'PERM', label: isArabic ? 'دائم' : 'Permanent' },
                ]}
                data-testid="ban-type-select"
              />
            </div>
            
            {banType === 'TEMP' && (
              <div className="form-group">
                <label>{isArabic ? 'مدة الحظر (أيام)' : 'Ban Duration (days)'}</label>
                <Input
                  type="number"
                  min="1"
                  max="365"
                  value={banDuration}
                  onChange={(e) => setBanDuration(parseInt(e.target.value) || 7)}
                  data-testid="ban-duration-input"
                />
              </div>
            )}
            
            <div className="form-group">
              <label>{isArabic ? 'سبب الحظر (مطلوب)' : 'Ban Reason (required)'}</label>
              <textarea
                className="ban-reason-textarea"
                value={banReason}
                onChange={(e) => setBanReason(e.target.value)}
                placeholder={isArabic ? 'اكتب سبب حظر المستخدم...' : 'Enter the reason for banning this user...'}
                rows={3}
                data-testid="ban-reason-input"
              />
            </div>
          </div>
          
          <AlertDialogFooter className="modalActions">
            <AlertDialogCancel 
              onClick={() => { setBanModalOpen(false); setBanTargetUser(null); setBanReason(''); }} 
              className="btn btnSecondaryOrange"
            >
              {t('cancel')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBanUser}
              disabled={!banReason.trim()}
              className="btn btn-danger"
              style={{ backgroundColor: '#EF4444', color: 'white' }}
            >
              <Ban size={16} />
              {banType === 'TEMP' 
                ? (isArabic ? `حظر لمدة ${banDuration} يوم` : `Ban for ${banDuration} days`)
                : (isArabic ? 'حظر دائم' : 'Permanent Ban')
              }
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Universal confirmation dialog for moderation actions (2026-02-27) */}
      <AlertDialog open={!!pendingAction} onOpenChange={(o) => { if (!o && !actionSubmitting) setPendingAction(null); }}>
        <AlertDialogContent data-testid="admin-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{isArabic ? 'تأكيد الإجراء' : 'Confirm action'}</AlertDialogTitle>
            <AlertDialogDescription data-testid="admin-confirm-label">
              {pendingAction?.label || (isArabic ? 'هل أنت متأكد؟' : 'Are you sure?')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          {pendingAction?.requiresReason && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '6px 0' }}>
              <label style={{ fontSize: 12, color: '#6b7280' }}>
                {isArabic ? 'السبب (مطلوب)' : 'Reason (required)'}
              </label>
              <input
                type="text"
                value={actionReason}
                onChange={(e) => setActionReason(e.target.value)}
                placeholder={isArabic ? 'مثال: مخالفة سياسة المحتوى' : 'e.g. policy_violation'}
                data-testid="admin-confirm-reason"
                style={{
                  background: 'var(--admin-bg-2, #f9fafb)',
                  border: '1px solid #e5e7eb',
                  borderRadius: 8,
                  padding: '8px 10px',
                  fontSize: 14,
                  outline: 'none',
                }}
              />
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionSubmitting} data-testid="admin-confirm-cancel">
              {isArabic ? 'إلغاء' : 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={runPendingAction}
              disabled={actionSubmitting}
              data-testid="admin-confirm-submit"
            >
              {actionSubmitting
                ? (isArabic ? 'جارٍ التنفيذ…' : 'Working…')
                : (isArabic ? 'تأكيد' : 'Confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default AdminModeration;
